import { anchorForIndex, moveCard, type NormalizedBoard } from '@blueprint/domain'
import { DragDropContext, type DropResult } from '@hello-pangea/dnd'
import { useMemo, useRef } from 'react'
import { Button } from '../../components/controls.js'
import { useBoardEditable, useBoardStore } from '../../state/board-store.js'
import { useColumnIds, useGroupIds, useSwimlaneIds } from '../../state/selectors.js'
import { filtersAreActive, useUiStore } from '../../state/ui-store.js'
import { ColumnHeaderRow, GroupHeaderRow } from './ColumnHeaders.js'
import { SwimlaneSection } from './SwimlaneSection.js'
import { useStoryMapActions } from './actions.js'
import { parseCellKey } from './VirtualCardList.js'
import styles from './StoryMap.module.css'

const GROUP_ROW_HEIGHT = 22

export function StoryMap() {
  const columnIds = useColumnIds()
  const swimlaneIds = useSwimlaneIds()
  const groupIds = useGroupIds()
  const actions = useStoryMapActions()
  const headerRef = useRef<HTMLDivElement>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  // A boolean subscription: card edits never rerender the map through this.
  // A filter hides cards, so dragging against it would reorder a list nobody can see;
  // a viewer may not reorder anything at all.
  const filtered = useUiStore((state) => filtersAreActive(state.filters))
  const editable = useBoardEditable()
  const dragDisabled = filtered || !editable
  const zoom = useUiStore((state) => state.zoom)

  const hasGroups = groupIds.length > 0

  const gridStyle = useMemo(
    () =>
      ({
        '--cols': columnIds.length,
        '--group-h': hasGroups ? `${GROUP_ROW_HEIGHT * zoom}px` : '0px',
        '--zoom': zoom,
      }) as React.CSSProperties,
    [columnIds.length, hasGroups, zoom],
  )

  const onDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result
    if (!destination) return
    const { rowId, columnId } = parseCellKey(destination.droppableId)
    if (!rowId || !columnId) return
    useBoardStore.getState().run((state: NormalizedBoard) => {
      const beforeId = anchorForIndex(state, rowId, columnId, destination.index, draggableId)
      return moveCard(state, draggableId, { rowId, columnId, beforeId })
    })
  }

  if (columnIds.length === 0 && swimlaneIds.length === 0) {
    return (
      <div className={styles.board}>
        <section className={styles.empty}>
          <h2 className={styles.emptyTitle}>This board is empty</h2>
          <p className={styles.emptyText}>
            Add a column for a step in the journey, and a swimlane to group the rows beneath it.
          </p>
          <div className={styles.controls}>
            <Button variant="primary" onClick={() => actions.addColumn()}>
              Add column
            </Button>
            <Button onClick={() => actions.addSwimlane()}>Add swimlane</Button>
          </div>
        </section>
      </div>
    )
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={styles.map} data-testid="board-map">
        <div
          ref={headerRef}
          className={styles.headerViewport}
          data-testid="board-headers"
          onFocusCapture={(event) => {
            // Copy focus-induced scrolling once the browser brings the control into view.
            // Mirroring every header scroll event would feed stale positions back during auto-scroll.
            const header = event.currentTarget
            requestAnimationFrame(() => {
              if (header.contains(document.activeElement) && boardRef.current) {
                boardRef.current.scrollLeft = header.scrollLeft
              }
            })
          }}
        >
          <div className={`${styles.grid} ${styles.headerGrid}`} style={gridStyle}>
            {hasGroups ? <GroupHeaderRow columnIds={columnIds} actions={actions} /> : null}
            <ColumnHeaderRow columnIds={columnIds} groupIds={groupIds} actions={actions} />
          </div>
        </div>
        <div
          ref={boardRef}
          className={styles.board}
          data-testid="board-scroller"
          onScroll={(event) => {
            const left = event.currentTarget.scrollLeft
            if (headerRef.current && headerRef.current.scrollLeft !== left) headerRef.current.scrollLeft = left
          }}
        >
          <div className={styles.grid} style={gridStyle}>

            {swimlaneIds.map((swimlaneId) => (
              <SwimlaneSection
                key={swimlaneId}
                swimlaneId={swimlaneId}
                swimlaneIds={swimlaneIds}
                columnIds={columnIds}
                actions={actions}
                dragDisabled={dragDisabled}
              />
            ))}

            {/* Sticky to the left edge: reachable without scrolling past every column. */}
            <div className={styles.footer}>
              <Button onClick={() => actions.addColumn()}>Add column</Button>
              <Button onClick={() => actions.addSwimlane()}>Add swimlane</Button>
            </div>
          </div>
        </div>
      </div>
    </DragDropContext>
  )
}
